import { useState } from 'react';
import { Car, Clock, AlertTriangle, CheckCircle, MapPin, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import { AddressAutocomplete, AddressData } from './AddressAutocomplete';
import { analytics } from '../lib/analytics';

interface TravelTimeResult {
  durationMinutes: number;
  durationText: string;
  bufferMinutes: number;
  totalMinutes: number;
  totalText: string;
  distanceKm: number;
  distanceText: string;
  cached: boolean;
}

interface TravelTimePreviewProps {
  homeAddress: string | null;
  hasCoordinates: boolean;
  /** Home coordinates for location bias */
  homeCoordinates?: { lat: number; lng: number } | null;
}

export function TravelTimePreview({ homeAddress, hasCoordinates, homeCoordinates }: TravelTimePreviewProps) {
  const [destination, setDestination] = useState<AddressData | null>(null);
  const [result, setResult] = useState<TravelTimeResult | null>(null);

  const testTravelTime = useMutation({
    mutationFn: (destinationAddress: string) =>
      api.post<TravelTimeResult>('/travel-time/preview', { destination: destinationAddress }),
    onSuccess: (data) => {
      setResult(data);
      analytics.featureUsed('travel_time_preview_calculated', {
        duration_minutes: data.durationMinutes,
        distance_km: data.distanceKm,
      });
    },
    onError: () => {
      setResult(null);
    },
  });

  const handleDestinationChange = (data: AddressData | null) => {
    setDestination(data);
    setResult(null);
  };

  const handleCalculate = () => {
    if (destination?.address) {
      testTravelTime.mutate(destination.address);
    }
  };

  if (!homeAddress) {
    return (
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Home address not set</p>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Add your home address above to enable travel time calculations for event reminders.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Home Address Status */}
      <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-green-800 dark:text-green-300">Home address configured</p>
            <p className="text-sm text-green-700 dark:text-green-400 mt-1 truncate" title={homeAddress}>
              {homeAddress}
            </p>
            {hasCoordinates ? (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Precise coordinates saved
              </p>
            ) : (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Using address text for travel calculations
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Destination Input */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-text-secondary">
          Test travel time to a destination
        </label>

        <AddressAutocomplete
          value={destination?.address || ''}
          onChange={handleDestinationChange}
          placeholder="Enter a destination address"
          locationBias={homeCoordinates || undefined}
        />

        {/* Always show Calculate button */}
        <button
          onClick={handleCalculate}
          disabled={!destination?.address || testTravelTime.isPending}
          className="w-full px-4 py-2.5 bg-soft-cyan text-white rounded-lg hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
        >
          {testTravelTime.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Car className="w-4 h-4" />
              Calculate Travel Time
            </>
          )}
        </button>
      </div>

      {/* Result Display */}
      {result && (
        <div className="p-4 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Car className="w-5 h-5 text-soft-cyan" />
              <span className="text-sm text-text-secondary">Drive time:</span>
              <span className="font-medium text-text-primary">{result.durationText}</span>
            </div>
            <div className="text-sm text-text-secondary">
              {result.distanceText}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-sky-200 dark:border-sky-800">
            <Clock className="w-5 h-5 text-soft-cyan" />
            <span className="text-sm text-text-secondary">Leave by time will be:</span>
            <span className="text-lg font-semibold text-text-primary">
              {result.totalMinutes} mins
            </span>
            <span className="text-sm text-text-muted">before event</span>
          </div>
          <p className="text-xs text-text-muted">
            Includes {result.bufferMinutes} min buffer for parking and walking to venue.
          </p>
        </div>
      )}

      {/* Error Display */}
      {testTravelTime.isError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Failed to calculate travel time</p>
              <p className="text-sm text-red-700 dark:text-red-400">
                {testTravelTime.error instanceof Error
                  ? testTravelTime.error.message
                  : 'Please check the addresses and try again.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
